import { Button } from '@iheartjane/reefer';
import type { Nullable } from '@iheartjane/types';
import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useFetcher } from 'react-router';

import { useHardRedirect } from '@jane/bloom/hooks/useHardRedirect';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { GoogleIdTokensResponse } from '@jane/bloom/routes/api.auth.id-tokens.google/types';
import {
  trackLoggedIn,
  trackRegistered,
} from '@jane/bloom/services/tracking/track';
import { logError } from '@jane/bloom/utils/logError';

import styles from './googleSignIn.module.css';

interface GoogleSignInButtonProps {
  googleSsoClientId?: Nullable<string>;
  /**
   * When provided, called on successful Google auth instead of a hard redirect,
   * keeping the user on the page (loaders still revalidate). Mirrors `useLogin`.
   */
  onSuccess?: (result: { redirectTo: string }) => void;
  skipBagAfterAuth?: boolean;
}

export const GoogleSignInButton = ({
  googleSsoClientId,
  onSuccess,
  skipBagAfterAuth,
}: GoogleSignInButtonProps) => {
  const googleButtonRef = useGoogleSignInButton({
    googleSsoClientId,
    onSuccess,
    skipBagAfterAuth,
  });

  return (
    <div
      ref={googleButtonRef}
      className={styles['googleButton']}
      data-testid="google-sign-in-button"
    >
      {/*
        Temporary loading button that gets replaced
        by the Google button once it's ready
      */}
      <Button
        full
        loading={true}
        variant="tertiary"
        style={{ height: '40px' }}
      />
    </div>
  );
};

/**
 * Initializes the Sign In With Google client.
 * Requires the external script to load on the page - https://accounts.google.com/gsi/client
 * @see https://developers.google.com/identity/gsi/web/reference/js-reference
 * @returns a ref to the div you want to replace with a Google sign-in button
 */
const useGoogleSignInButton = ({
  googleSsoClientId,
  onSuccess,
  skipBagAfterAuth,
}: GoogleSignInButtonProps): RefObject<HTMLDivElement> => {
  const ref = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { featureFlags } = useRootLoaderData();
  const fetcher = useFetcher<GoogleIdTokensResponse>();
  const hardRedirect = useHardRedirect();

  const submitToken = useCallback(
    async (response: google.accounts.id.CredentialResponse) => {
      fetcher.submit(
        { ...response, skipBag: `${skipBagAfterAuth}` },
        {
          action: `/api/auth/id-tokens/google`,
          method: 'POST',
        }
      );
    },
    [fetcher, skipBagAfterAuth]
  );

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data?.user) {
      const { new_registration } = fetcher.data.user;

      if (new_registration) {
        trackRegistered(
          {
            googleSsoEnabled: true,
            method: 'google',
            source: fetcher.data.authSource,
          },
          featureFlags
        );
      } else {
        trackLoggedIn(
          {
            googleSsoEnabled: true,
            method: 'google',
            source: fetcher.data.authSource,
          },
          featureFlags
        );
      }

      if (onSuccess) {
        onSuccess({ redirectTo: fetcher.data.redirectTo });
      } else {
        hardRedirect(fetcher.data.redirectTo);
      }
    }
  }, [featureFlags, fetcher.data, fetcher.state, hardRedirect, onSuccess]);

  useEffect(() => {
    if (!googleSsoClientId) return;
    if (!ref.current) return;

    const currentWidth = ref.current.clientWidth || 400;

    // Function to wait for Google API to be available
    const waitForGoogleApi = (): Promise<void> => {
      return new Promise((resolve) => {
        const checkGoogleApi = () => {
          if (window.google?.accounts?.id) {
            timeoutRef.current = null;
            resolve();
          } else {
            timeoutRef.current = setTimeout(checkGoogleApi, 100); // Check every 100ms
          }
        };
        checkGoogleApi();
      });
    };

    const initializeGoogleSignIn = async () => {
      try {
        // Wait for Google API to be available
        await waitForGoogleApi();

        window.google?.accounts.id.initialize({
          auto_select: true,
          callback: submitToken,
          client_id: googleSsoClientId,
          context: 'use',
          itp_support: true,
          ux_mode: 'popup',
        });

        // Only proceed with rendering if ref is still available
        if (ref.current) {
          window.google?.accounts.id.renderButton(ref.current, {
            logo_alignment: 'left',
            shape: 'pill',
            size: 'large',
            text: 'signin_with',
            theme: 'outline',
            type: 'standard',
            width: currentWidth,
          });
        }
      } catch (error) {
        logError(error);
      }
    };

    initializeGoogleSignIn();

    // Cleanup function to cancel any pending operations
    return () => {
      window.google?.accounts.id.cancel();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [googleSsoClientId, submitToken]);

  return ref;
};
